import { UserInfoWrapper } from "@/components/UserInfo/UserInfoWrapper";
import { useProjectRoleContext } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { AddEditDefaultUserModal } from "@/containers/Admin/ProjectRoles/components/RegionalRole/Modals";
import { FieldNames, FormType } from "@/enums";
import { nameof } from "@/helpers";
import { IUser } from "@/interfaces";
import { IDefaultProjectUser } from "@/interfaces/Project/Roles/IDefaultProjectUser";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useGetUsersByIdsMutation } from "@/services";
import { faEdit, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Table, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IProps {
  regionRole: IRegionalProjectRole;
}

export const DefaultUsersTab = ({ regionRole }: IProps) => {
  const intl = useIntl();

  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const { updateRegionalRole } = useProjectRoleContext();
  const [workingDefaultUser, setWorkingDefaultUser] = useState<string>("");
  const [getUsersByIds, { data: usersByIds, isLoading }] =
    useGetUsersByIdsMutation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [defaultUsers, setDefaultUsers] = useState<IDefaultProjectUser[]>([]);

  useEffect(() => {
    if (!regionRole?.defaultProjectUsers) return;

    setDefaultUsers(regionRole.defaultProjectUsers);
  }, [regionRole?.defaultProjectUsers]);

  useEffect(() => {
    if (!defaultUsers) return;

    const userIds = defaultUsers.map((user) => user.userId);
    getUsersByIds(userIds);
  }, [defaultUsers, getUsersByIds]);

  const addUser = (newUser: IDefaultProjectUser): void => {
    const updatedRegionRole = { ...regionRole };
    updatedRegionRole.defaultProjectUsers = [newUser, ...defaultUsers];
    if (workingDefaultUser !== "") {
      updatedRegionRole.defaultProjectUsers = [
        newUser,
        ...defaultUsers.filter((x) => x.id !== workingDefaultUser),
      ];
    } else {
      updatedRegionRole.defaultProjectUsers = [newUser, ...defaultUsers];
    }

    updateRegionalRole(updatedRegionRole);
    setModalOpen(false);
  };

  const deleteUser = (id: string): void => {
    const updatedRegionRole = { ...regionRole };
    updatedRegionRole.defaultProjectUsers = defaultUsers.filter(
      (user) => user?.id !== id
    );

    updateRegionalRole(updatedRegionRole);
  };

  const editDefaultUser = (id: string): void => {
    setWorkingDefaultUser(id);
    setModalOpen(true);
  };

  const closeEditDefaultUser = (): void => {
    setWorkingDefaultUser("");
    setModalOpen(false);
  };

  const columns: ColumnsType<IDefaultProjectUser> = [
    {
      title: intl.formatMessage({ id: "projectRoles.name" }),
      dataIndex: nameof<IDefaultProjectUser>("userId"),
      render: (userId) => {
        const user = usersByIds?.find((x: IUser) => x.id === userId);
        return (
          <UserInfoWrapper user={user}>{user?.displayName}</UserInfoWrapper>
        );
      },
    },
    {
      title: intl.formatMessage({ id: "projectRoles.projectType" }),
      dataIndex: nameof<IDefaultProjectUser>("publicProjectTypeIds"),
      render: (projectTypes) =>
        projectTypes?.map((tag: string) => (
          <Tag color={"blue"} key={tag}>
            {getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProjectType,
              tag
            )}
          </Tag>
        )),
    },
    {
      title: intl.formatMessage({ id: "projectRoles.productCategory" }),
      dataIndex: nameof<IDefaultProjectUser>("publicProductCategoryIds"),
      render: (productCategories) =>
        productCategories?.map((tag: string) => (
          <Tag color={"blue"} key={tag}>
            {getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProductCategory,
              tag
            )}
          </Tag>
        )),
    },
    {
      title: intl.formatMessage({ id: "projectRoles.purpose" }),
      dataIndex: nameof<IDefaultProjectUser>("purposes"),
      render: (purposes) =>
        purposes?.map((purpose: string) => (
          <Tag color={"blue"} key={purpose}>
            {purpose}
          </Tag>
        )),
    },
    {
      title: intl.formatMessage({ id: "projectRoles.action" }),
      dataIndex: nameof<IDefaultProjectUser>("id"),
      render: (id: string) => (
        <>
          <Button
            data-test-id={`default-users-edit-${id}`}
            type="link"
            onClick={(): void => editDefaultUser(id)}
            icon={<FontAwesomeIcon icon={faEdit} />}
          ></Button>
          <Button
            data-test-id={`default-users-delete-${id}`}
            type="link"
            onClick={(): void => deleteUser(id)}
            icon={<FontAwesomeIcon icon={faTrash} />}
          ></Button>
        </>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" className="pl-1">
        <Col>
          <Typography.Title level={5}>
            {intl.formatMessage({ id: "projectRoles.defaultProjectUsers" })}
          </Typography.Title>
        </Col>
        <Col>
          <Button
            data-test-id="add-default-user"
            onClick={(): void => setModalOpen(true)}
          >
            {intl.formatMessage({ id: "projectRoles.addUser" })}
          </Button>
        </Col>
      </Row>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={defaultUsers}
        rowKey={({ id }) => id}
      />
      <AddEditDefaultUserModal
        open={modalOpen}
        onClose={(): void => closeEditDefaultUser()}
        onOk={addUser}
        defaultUserId={workingDefaultUser}
        regionRole={regionRole}
      />
    </>
  );
};
